import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';

const TIPO_OPTIONS = [
  'Amministrazione',
  'Attività',
  'Progetti',
  'Gestione Sede',
  'Comunicazione',
  'Contabilità',
  'Sito Web',
  'Spesa F&B',
  'Commissioni varie',
  'Progettazione attività',
  'Sviluppo',
  'Gestione Associativa',
];

export default function TimebankNew() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated, userData } = useAuth();
  const formBg = useColorModeValue('white', 'gray.800');
  const formBorder = useColorModeValue('gray.200', 'gray.700');

  const soci = useMemo(() => {
    const source = Array.isArray(userData?.tesseramenti)
      ? userData.tesseramenti
      : [];
    return source
      .map((socio) => ({
        id: socio?.documentId,
        label:
          `${socio?.nomeSocio || ''} ${socio?.cognomeSocio || ''}`.trim() ||
          socio?.documentId,
      }))
      .filter((socio) => Boolean(socio.id));
  }, [userData]);

  const hasMultipleSoci = soci.length > 1;
  const singleSocioId = soci.length === 1 ? soci[0].id : '';

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    attivita: '',
    tipo: 'Amministrazione',
    numeroOre: '',
    note: '',
    socio: '',
    costoOrario: 10,
    volontario: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) return null;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const parsedNumeroOre = Number(
      String(formData.numeroOre).replace(',', '.')
    );

    if (
      !formData.date ||
      !formData.attivita ||
      Number.isNaN(parsedNumeroOre) ||
      parsedNumeroOre <= 0
    ) {
      toast({
        title: 'Compila i campi obbligatori',
        description: 'Data, attività e numero ore devono essere valorizzati.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (soci.length === 0) {
      toast({
        title: 'Nessun socio associato',
        description: 'Impossibile inserire una voce banca ore senza socio.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (hasMultipleSoci && !formData.socio) {
      toast({
        title: 'Seleziona un socio',
        description:
          'Con più soci associati devi scegliere il socio per la voce.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        '/api/timebanks',
        {
          date: formData.date,
          attivita: formData.attivita,
          tipo: formData.tipo,
          numeroOre: parsedNumeroOre,
          note: formData.note,
          socio: hasMultipleSoci ? formData.socio : singleSocioId,
          costoOrario: formData.costoOrario,
          volontario: formData.volontario,
        },
        { withCredentials: true }
      );

      toast({
        title: 'Voce creata',
        description: 'La voce banca ore è stata inserita correttamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/account');
    } catch (error) {
      toast({
        title: 'Errore salvataggio',
        description:
          error.response?.data?.error ||
          'Impossibile creare la voce banca ore.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container
      maxW="container.lg"
      py={{ base: 4, md: 8 }}
      px={{ base: 4, md: 6 }}
    >
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        <Heading size={{ base: 'md', md: 'lg' }}>Nuova voce banca ore</Heading>

        <Box
          bg={formBg}
          borderWidth="1px"
          borderColor={formBorder}
          borderRadius={{ base: 'lg', md: 'xl' }}
          px={{ base: 4, md: 8 }}
          py={{ base: 5, md: 8 }}
          boxShadow={{ base: 'sm', md: 'md' }}
        >
          <VStack spacing={{ base: 4, md: 5 }} align="stretch">
            {soci.length === 0 && (
              <Alert status="warning">
                <AlertIcon />
                Non risultano soci associati al tuo account.
              </Alert>
            )}

            <FormControl isRequired>
              <FormLabel>Data</FormLabel>
              <Input
                type="date"
                value={formData.date}
                onChange={({ target: { value } }) =>
                  handleChange('date', value)
                }
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Attività</FormLabel>
              <Input
                value={formData.attivita}
                onChange={({ target: { value } }) =>
                  handleChange('attivita', value)
                }
                placeholder="Descrizione attività"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Tipo</FormLabel>
              <Select
                value={formData.tipo}
                onChange={({ target: { value } }) =>
                  handleChange('tipo', value)
                }
              >
                {TIPO_OPTIONS.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Numero ore</FormLabel>
              <Input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                value={formData.numeroOre}
                onChange={({ target: { value } }) =>
                  handleChange('numeroOre', value)
                }
                placeholder="Es. 1.5"
              />
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={formData.volontario}
                onChange={({ target: { checked } }) =>
                  setFormData((prev) => ({
                    ...prev,
                    volontario: checked,
                  }))
                }
              >
                Volontario
              </Checkbox>
            </FormControl>

            <FormControl>
              <FormLabel>Note</FormLabel>
              <Input
                value={formData.note}
                onChange={({ target: { value } }) =>
                  handleChange('note', value)
                }
                placeholder="Note opzionali"
              />
            </FormControl>

            {hasMultipleSoci && (
              <FormControl isRequired>
                <FormLabel>Socio</FormLabel>
                <Select
                  placeholder="Seleziona socio"
                  value={formData.socio}
                  onChange={({ target: { value } }) =>
                    handleChange('socio', value)
                  }
                >
                  {soci.map((socio) => (
                    <option key={socio.id} value={socio.id}>
                      {socio.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              colorScheme="teal"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              w={{ base: '100%', md: 'auto' }}
              alignSelf={{ base: 'stretch', md: 'flex-start' }}
            >
              Crea voce
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
}
